import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SimpleMapView } from '@/components/displays/SimpleMapView';
import { DisplayList } from '@/components/displays/DisplayList';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Filter, MapPin, Navigation, Menu, X } from 'lucide-react';
import NavBar from '@/components/NavBar';

interface Display {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  holiday_type: string;
  display_type: string;
  image_url: string;
  featured: boolean;
  upvote_count: number;
}

const HOLIDAY_FILTERS = [
  'Christmas',
  'Halloween',
  'Easter',
  'Fourth of July',
  'Thanksgiving',
  'Valentine\'s Day'
];

export default function Displays() {
  const navigate = useNavigate();

  // Core state
  const [allDisplays, setAllDisplays] = useState<Display[]>([]);
  const [filteredDisplays, setFilteredDisplays] = useState<Display[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Filter state
  const [selectedHolidays, setSelectedHolidays] = useState<string[]>([]);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  // User location
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load displays from Supabase
  const loadDisplays = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: supabaseError } = await supabase
        .from('displays')
        .select('*')
        .eq('status', 'active')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (supabaseError) {
        throw new Error(supabaseError.message);
      }

      const displays: Display[] = (data || []).map(item => ({
        id: String(item.id),
        name: item.name || '',
        description: item.description || '',
        latitude: item.latitude,
        longitude: item.longitude,
        city: item.city || '',
        state: item.state || '',
        holiday_type: item.holiday_type || '',
        display_type: item.display_type || '',
        image_url: item.image_url || '',
        featured: item.featured || false,
        upvote_count: item.upvote_count || 0
      }));

      setAllDisplays(displays);
      setFilteredDisplays(displays);
    } catch (err) {
      console.error('Failed to load displays:', err);
      setError(err instanceof Error ? err.message : 'Failed to load displays');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load displays on mount
  useEffect(() => {
    loadDisplays();
  }, [loadDisplays]);

  // Apply filters
  useEffect(() => {
    let filtered = [...allDisplays];

    // Filter by holiday type
    if (selectedHolidays.length > 0) {
      filtered = filtered.filter(display =>
        selectedHolidays.some(holiday =>
          display.holiday_type.toLowerCase().includes(holiday.toLowerCase())
        )
      );
    }

    // Filter by featured
    if (showFeaturedOnly) {
      filtered = filtered.filter(display => display.featured);
    }

    setFilteredDisplays(filtered);
  }, [allDisplays, selectedHolidays, showFeaturedOnly]);

  // Get user location
  const getUserLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    setIsGettingLocation(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000 // 5 minutes
        });
      });

      setUserLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
    } catch (err) {
      console.warn('Failed to get location:', err);
    } finally {
      setIsGettingLocation(false);
    }
  }, []);

  // Get location on mount
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  // Handle display interactions
  const handleDisplayClick = useCallback((display: Display) => {
    // Just close sidebar on mobile when clicking a display
    if (isMobile && showSidebar) {
      setShowSidebar(false);
    }
  }, [isMobile, showSidebar]);

  const handleDisplayViewDetails = useCallback((display: Display) => {
    navigate(`/display/${display.id}`);
  }, [navigate]);

  // Filter handlers
  const toggleHolidayFilter = useCallback((holiday: string) => {
    setSelectedHolidays(prev =>
      prev.includes(holiday)
        ? prev.filter(h => h !== holiday)
        : [...prev, holiday]
    );
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedHolidays([]);
    setShowFeaturedOnly(false);
  }, []);

  // Sidebar content
  const SidebarContent = () => (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Filters</h2>
        {(selectedHolidays.length > 0 || showFeaturedOnly) && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            Clear All
          </Button>
        )}
      </div>

      {/* Featured Filter */}
      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showFeaturedOnly}
            onChange={(e) => setShowFeaturedOnly(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm">Featured displays only</span>
        </label>
      </div>

      {/* Holiday Filters */}
      <div>
        <h3 className="font-medium mb-3">Holiday Type</h3>
        <div className="space-y-2">
          {HOLIDAY_FILTERS.map(holiday => (
            <label key={holiday} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedHolidays.includes(holiday)}
                onChange={() => toggleHolidayFilter(holiday)}
                className="rounded border-border"
              />
              <span className="text-sm">{holiday}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="h-screen flex flex-col">
        <NavBar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to load displays</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadDisplays}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-screen flex flex-col">
        <NavBar />

        {/* Mobile header */}
        <div className="bg-background border-b px-4 py-3 mt-16">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">Light Displays</h1>
              <p className="text-xs text-muted-foreground">
                {isLoading ? 'Loading...' : `${filteredDisplays.length} displays`}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={getUserLocation}
                disabled={isGettingLocation}
              >
                {isGettingLocation ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Navigation className="h-4 w-4" />
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSidebar(true)}
              >
                <Menu className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Full screen map */}
        <div className="flex-1">
          <SimpleMapView
            displays={filteredDisplays}
            userLocation={userLocation}
            onDisplayClick={handleDisplayClick}
          />
        </div>

        {/* Mobile sidebar */}
        <Sheet open={showSidebar} onOpenChange={setShowSidebar}>
          <SheetContent side="left" className="w-[300px]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Displays</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSidebar(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <SidebarContent />

            <div className="mt-6 border-t pt-4">
              <DisplayList
                displays={filteredDisplays}
                isLoading={isLoading}
                onDisplayClick={handleDisplayClick}
                onDisplayViewDetails={handleDisplayViewDetails}
                className="h-[400px]"
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="h-screen flex flex-col">
      <NavBar />

      {/* Desktop header */}
      <div className="bg-background border-b px-4 py-3 mt-16">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Holiday Light Displays</h1>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading displays...' : `${filteredDisplays.length} displays found`}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={getUserLocation}
              disabled={isGettingLocation}
              className="flex items-center gap-2"
            >
              {isGettingLocation ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Finding...
                </>
              ) : (
                <>
                  <Navigation className="h-4 w-4" />
                  Find Near Me
                </>
              )}
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {(selectedHolidays.length > 0 || showFeaturedOnly) && (
                    <span className="ml-2 bg-primary text-primary-foreground rounded-full w-2 h-2" />
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[400px]">
                <SidebarContent />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main content - side by side */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - display list */}
        <div className="w-1/3 border-r">
          <DisplayList
            displays={filteredDisplays}
            isLoading={isLoading}
            onDisplayClick={handleDisplayClick}
            onDisplayViewDetails={handleDisplayViewDetails}
          />
        </div>

        {/* Right side - map */}
        <div className="flex-1">
          <SimpleMapView
            displays={filteredDisplays}
            userLocation={userLocation}
            onDisplayClick={handleDisplayClick}
          />
        </div>
      </div>
    </div>
  );
}